import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { StudyShell } from './StudyShell';
import { readSelfAssessment, selfAssessmentKey } from './studySelfAssessmentApi';
import { StudySelfAssessmentDraft } from './StudySelfAssessmentDraft';
import { StudySelfAssessmentAnswers } from './StudySelfAssessmentAnswers';
export function StudySelfAssessmentPage() {
  const { analysisId = '', setId = '' } = useParams();
  const [params] = useSearchParams();
  const subject = params.get('subject') ?? 'mathematics';
  const paper = useQuery({
    queryKey: selfAssessmentKey(analysisId, setId),
    queryFn: () => readSelfAssessment(analysisId, setId),
    retry: false,
    refetchInterval: (q) => (q.state.data?.status === 'generating' ? 5000 : false),
  });
  const value = paper.data;
  return (
    <StudyShell
      mobileTitle="Self-assessment"
      mobileBack={{
        to: `/learn/study/subjects/${encodeURIComponent(subject)}?tab=assessments`,
        label: 'Back to Assessments',
      }}
    >
      <Link
        className="hidden text-sm font-bold underline sm:inline-flex"
        to={`/learn/study/subjects/${encodeURIComponent(subject)}?tab=assessments`}
      >
        Back to Assessments
      </Link>
      {paper.isLoading && (
        <p role="status" className="mt-6">
          Opening saved assessment…
        </p>
      )}
      {paper.error && (
        <p role="alert" className="mt-6 rounded-xl bg-wash-coral p-4">
          {paper.error.message}{' '}
          <button className="font-bold underline" onClick={() => void paper.refetch()}>
            Retry
          </button>
        </p>
      )}
      {value && (
        <div className="mx-auto mt-5 max-w-4xl text-ink">
          <h1 className="text-3xl font-extrabold">{value.assessment.title}</h1>
          <p className="mt-3 text-sm">
            {value.assessment.question_count} questions · {value.assessment.difficulty} ·{' '}
            {value.assessment.question_kinds.map((k) => k.replace('_', ' ')).join(', ')} ·{' '}
            {value.phase === 'draft' ? 'Draft preview' : 'Saved self-assessment'}
          </p>
          <p className="mt-2 text-sm">
            Scope:{' '}
            {value.assessment.key_point_indices.length
              ? 'selected source points'
              : 'whole analysed material'}
            .{' '}
            <Link
              className="font-bold underline"
              to={`/learn/study/materials/${encodeURIComponent(value.document_id)}`}
            >
              Open source material
            </Link>
          </p>
          {isLocalStudyExperience() && (
            <p className="mt-3 rounded-xl bg-wash-sunshine p-3 text-sm">
              Fictional pre-generated assessment and feedback. Demo changes stay in this tab; reset,
              reload or exit restores the examples. No live AI or Stars.
            </p>
          )}
          <button
            className="mt-3 min-h-11 text-sm font-bold underline"
            onClick={() => void paper.refetch()}
          >
            Refresh saved result
          </button>
          {value.status === 'generating' ? (
            <p role="status" className="mt-5">
              Preparing questions. This saved request will reopen here; do not create another
              request.
            </p>
          ) : value.status === 'failed' ? (
            <p role="alert" className="mt-5 rounded-xl bg-wash-coral p-4">
              Generation failed.{' '}
              {value.refunded
                ? 'Its Stars were refunded.'
                : 'Any unsettled refund needs to finish before another paid request.'}{' '}
              Return to Assessments to review the settings.
            </p>
          ) : value.phase === 'draft' ? (
            <StudySelfAssessmentDraft key={value.id} paper={value} />
          ) : (
            <StudySelfAssessmentAnswers
              key={`${value.id}:${params.get('question') ?? ''}`}
              paper={value}
              subjectKey={subject}
              questionId={params.get('question') ?? undefined}
            />
          )}
        </div>
      )}
    </StudyShell>
  );
}
